import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, LogOut, Receipt, Gift, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { walletService, WalletBalance, WalletTransactionItem } from '../../services/wallet/walletService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar, type SidebarMenuItem } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { CalendarDatePicker } from '../../components/CalendarDatePicker';
import { authService } from '../../services/auth/authService';
import { apiClient } from '../../services/api/apiClient';
import AddFundsModal from './AddFundsModal';
import '../../pages/dashboard/Dashboard.css';
import './BillingPage.css';

const PAGE_SIZE = 10;

function formatTableDate(createdAt: string): string {
  try {
    const d = new Date(createdAt);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return createdAt;
  }
}

function formatTxDescription(tx: WalletTransactionItem): string {
  const type = tx.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return type;
}

function formatTxSubDescription(tx: WalletTransactionItem): string {
  if (tx.reference_id) return `Ref: ${tx.reference_id}`;
  if (tx.reference_type) return tx.reference_type;
  return '';
}

/** Deep links for redirecting back to the app after Cashfree payment (iOS/Android webview). Set in .env. */
const DEEP_LINK_IOS = process.env.REACT_APP_WALLET_RETURN_DEEP_LINK_IOS || '';
const DEEP_LINK_ANDROID = process.env.REACT_APP_WALLET_RETURN_DEEP_LINK_ANDROID || '';

export default function BillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransactionItem[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rechargeSuccess, setRechargeSuccess] = useState<string | null>(null);
  const [rechargeCancelled, setRechargeCancelled] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const from402 = (location.state as any)?.from402 === true;
  const message402 = (location.state as any)?.message as string | undefined;

  const loadBalance = useCallback(async () => {
    const bal = await walletService.getBalance();
    setBalance(bal);
  }, []);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tx = await walletService.getTransactions(
        page * PAGE_SIZE,
        PAGE_SIZE,
        fromDate || undefined,
        toDate || undefined
      );
      setTransactions(tx.transactions);
      setTotalTransactions(tx.total);
    } catch (e: any) {
      const msg = e?.response?.data?.detail?.message ?? e?.message ?? 'Failed to load transactions.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadBalance();
      } catch {
        if (!cancelled) setError('Failed to load balance.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadBalance]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // After Razorpay/Cashfree payment, lands here with ?recharge_status=success|cancelled&order_id=...
  useEffect(() => {
    const status = searchParams.get('recharge_status');
    const orderId = searchParams.get('order_id');
    const platform = (searchParams.get('platform') || '').toLowerCase();

    if (status === 'success') {
      setRechargeSuccess(`Payment successful! Credits have been added to your wallet (Order: ${orderId}).`);
      // Clear params from URL
      navigate('/billing', { replace: true });
      // Refresh balance and transactions
      loadBalance();
      loadTransactions();
    } else if (status === 'cancelled') {
      setRechargeCancelled('Payment was cancelled. No charges were made.');
      navigate('/billing', { replace: true });
    }

    // Legacy deep links for mobile app webview
    if (!orderId || (platform !== 'ios' && platform !== 'android')) return;
    const deepLink = platform === 'ios' ? DEEP_LINK_IOS : DEEP_LINK_ANDROID;
    if (!deepLink) return;
    const sep = deepLink.includes('?') ? '&' : '?';
    const url = `${deepLink}${sep}order_id=${encodeURIComponent(orderId)}`;
    window.location.replace(url);
  }, [searchParams, navigate, loadBalance, loadTransactions]);

  const handleGoToProfile = () => navigate('/profile');
  const handleLogout = () => {
    authService.clearTokens();
    window.location.replace('/login');
  };

  const handleRechargeSuccess = (orderId: string, checkoutUrl: string) => {
    setIsModalOpen(false);
    // checkoutUrl from backend bridge is relative /api/v1/wallet/checkout/...
    // We need to resolve it to an absolute URL
    // Try to get API base from env, otherwise fallback to standard prod API domain if on zivohealth.ai
    let baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    if (!baseUrl) {
      if (window.location.hostname === 'doctor.zivohealth.ai') {
        baseUrl = 'https://api.zivohealth.ai';
      } else {
        baseUrl = window.location.origin;
      }
    }
    const finalUrl = checkoutUrl.startsWith('/') ? `${baseUrl}${checkoutUrl}` : checkoutUrl;
    window.location.href = finalUrl;
  };

  const totalPages = Math.max(1, Math.ceil(totalTransactions / PAGE_SIZE));
  const startItem = totalTransactions === 0 ? 0 : page * PAGE_SIZE + 1;
  const endItem = Math.min((page + 1) * PAGE_SIZE, totalTransactions);

  const sidebarItems: SidebarMenuItem[] = [
    {
      id: 'wallet',
      label: 'My Wallet',
      icon: '💳',
      iconElement: <CreditCard size={20} strokeWidth={2} />,
      onClick: () => {},
      isActive: true,
      isProfileCard: true,
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: '↩️',
      iconElement: <LogOut size={20} strokeWidth={2} />,
      onClick: handleLogout,
      isLogout: true,
    },
  ];

  return (
    <div className="dashboard">
      <DashboardHeader
        onProfileClick={handleGoToProfile}
        onBillingClick={() => navigate('/billing')}
        onLogout={handleLogout}
      />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="wallet-page-card">
              <div className="wallet-overview-page billing-page">
              {from402 && message402 && (
                <div className="wallet-alert warning" role="alert">
                  <strong>Insufficient balance</strong>
                  <p style={{ margin: '6px 0 0' }}>{message402}</p>
                  <p style={{ margin: '8px 0 0', fontSize: '0.875rem' }}>Add funds below to continue.</p>
                </div>
              )}

              {rechargeSuccess && (
                <div className="wallet-alert success" role="alert">
                  {rechargeSuccess}
                </div>
              )}

              {rechargeCancelled && (
                <div className="wallet-alert warning" role="alert">
                  {rechargeCancelled}
                </div>
              )}

              {error && (
                <div className="wallet-alert error" role="alert">
                  {error}
                </div>
              )}

              <div className="wallet-overview-header">
                <div>
                  <h1 className="wallet-overview-title">Wallet overview</h1>
                  <p className="wallet-overview-subtitle">
                    Manage your wallet balance and review account activity.
                  </p>
                </div>
                <button
                  type="button"
                  className="wallet-add-funds-btn"
                  onClick={() => setIsModalOpen(true)}
                  aria-label="Add funds"
                >
                  + Add Funds
                </button>
              </div>

              {loading && !balance ? (
                <p className="wallet-loading">Loading…</p>
              ) : (
                <>
                  {balance != null && (() => {
                    const paidCredits = balance.balance_usd;
                    const freeCredits = balance.credit_balance_usd ?? 0;
                    const totalCredits = paidCredits + freeCredits;
                    const expiryText = balance.credit_balance_expiry 
                      ? `EXP: ${new Date(balance.credit_balance_expiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}`
                      : 'EXP: NONE';

                    return (
                      <div className="wallet-cards-row">
                        <div className="wallet-balance-card available">
                          <div className="wallet-balance-card-icon">G</div>
                          <div className="wallet-balance-card-label">AVAILABLE BALANCE</div>
                          <div className="wallet-balance-card-value">
                            {Number(totalCredits).toFixed(2)} Credits
                          </div>
                        </div>
                        <div className="wallet-balance-card">
                          <div className="wallet-balance-card-icon">
                            <Receipt size={20} />
                          </div>
                          <div className="wallet-balance-card-label">PAID CREDITS</div>
                          <div className="wallet-balance-card-value">
                            {Number(paidCredits).toFixed(2)}
                          </div>
                        </div>
                        <div className="wallet-balance-card">
                          <div className="wallet-balance-card-icon">
                            <Gift size={20} />
                          </div>
                          <div className="wallet-balance-card-label">FREE CREDITS</div>
                          <div className="wallet-balance-card-value-row">
                            <span className="wallet-balance-card-value">
                              {Number(freeCredits).toFixed(2)}
                            </span>
                            <span 
                              className="wallet-balance-card-expiry" 
                              style={{ color: freeCredits > 0 ? '#E62060' : undefined }}
                            >
                              {expiryText}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <section className="wallet-history-section">
                    <h2 className="wallet-history-title">Transaction History</h2>
                    <div className="wallet-history-filters">
                      <div className="wallet-history-filters-left">
                        <div className="wallet-history-filter-from">
                          <CalendarDatePicker
                            label="FROM"
                            value={fromDate}
                            onChange={(date) => {
                              setFromDate(date);
                              setPage(0);
                            }}
                            placeholder="dd/mm/yyyy"
                            dateDisplayFormat="short"
                          />
                        </div>
                        <div className="wallet-history-filter-to">
                          <CalendarDatePicker
                            label="TO"
                            value={toDate}
                            onChange={(date) => {
                              setToDate(date);
                              setPage(0);
                            }}
                            placeholder="dd/mm/yyyy"
                            dateDisplayFormat="short"
                          />
                        </div>
                      </div>
                      <div className="wallet-history-filters-right">
                        <div className="wallet-history-status-select">
                          <label
                            className="wallet-history-status-label"
                            htmlFor="wallet-status-select"
                          >
                            Status
                          </label>
                          <div className="wallet-history-status-control">
                            <select
                              id="wallet-status-select"
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                              aria-label="Status"
                            >
                              <option value="all">All status</option>
                              <option value="completed">Completed</option>
                              <option value="pending">Pending</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="wallet-filter-btn"
                          aria-label="More filters"
                        >
                          <SlidersHorizontal size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="wallet-table-card">
                      {loading && balance ? (
                        <div className="wallet-table-loading">
                          Loading transactions…
                        </div>
                      ) : (
                        <>
                          <table className="wallet-table">
                            <thead>
                              <tr>
                                <th>DATE</th>
                                <th>DESCRIPTION</th>
                                <th>AMOUNT</th>
                                <th>STATUS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="wallet-table-empty-state wallet-table-empty-state-cell">
                                    No transactions yet.
                                  </td>
                                </tr>
                              ) : (
                                transactions.map((tx) => (
                                  <tr key={tx.id}>
                                    <td className="wallet-tx-date">
                                      {formatTableDate(tx.created_at)}
                                    </td>
                                    <td>
                                      <div className="wallet-tx-desc-primary">
                                        {formatTxDescription(tx)}
                                      </div>
                                      {formatTxSubDescription(tx) && (
                                        <div className="wallet-tx-desc-secondary">
                                          {formatTxSubDescription(tx)}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          tx.type === 'credit'
                                            ? 'wallet-tx-amount-credit'
                                            : 'wallet-tx-amount-debit'
                                        }
                                      >
                                        {tx.type === 'credit' ? '+' : '-'}$
                                        {Math.abs(tx.amount_usd).toFixed(2)}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="wallet-status-pill completed">
                                        Completed
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                          {totalTransactions > 0 && (
                            <div className="wallet-pagination">
                              <span className="wallet-pagination-info">
                                Showing {startItem} to {endItem} of {totalTransactions} transactions
                              </span>
                              <div className="wallet-pagination-controls">
                                <button
                                  type="button"
                                  className="wallet-pagination-btn"
                                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                                  disabled={page === 0}
                                  aria-label="Previous page"
                                >
                                  <ChevronLeft size={18} />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i)
                                  .filter((i) => i >= Math.max(0, page - 2) && i <= Math.min(totalPages - 1, page + 2))
                                  .map((i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      className={`wallet-pagination-btn ${i === page ? 'active' : ''}`}
                                      onClick={() => setPage(i)}
                                    >
                                      {i + 1}
                                    </button>
                                  ))}
                                <button
                                  type="button"
                                  className="wallet-pagination-btn"
                                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                  disabled={page >= totalPages - 1}
                                  aria-label="Next page"
                                >
                                  <ChevronRight size={18} />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </section>
                </>
              )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <AddFundsModal 
        open={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSuccess={handleRechargeSuccess} 
      />
    </div>
  );
}
